import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Divider,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  ArrowBack as BackIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchInputById,
  fetchInputTypes,
  createInput,
  updateInputAction,
  setSelectedInput,
  setValidationErrors,
} from '../../store/reducers/inputsSlice';
import { CreateInputRequest, UpdateInputRequest } from '../../services/inputsApi';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  form: {
    '& .MuiTextField-root': {
      marginBottom: theme.spacing(2),
    },
    '& .MuiFormControl-root': {
      marginBottom: theme.spacing(2),
    },
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
}));

const InputsEdit: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  
  const isEditing = Boolean(id);
  const inputId = id ? parseInt(id, 10) : null;

  const {
    selectedInput,
    inputTypes,
    loading,
    error,
    validationErrors,
  } = useSelector((state: RootState) => state.inputs);

  // Form state
  const [formData, setFormData] = useState({
    Name: '',
    InputTypeId: 0,
    Active: true,
  });

  // Load data on component mount
  useEffect(() => {
    dispatch(fetchInputTypes());
    
    if (isEditing && inputId) {
      dispatch(fetchInputById(inputId));
    } else {
      dispatch(setSelectedInput(null));
    }

    // Clear validation errors
    dispatch(setValidationErrors([]));
  }, [dispatch, isEditing, inputId]);

  // Update form data when selectedInput changes
  useEffect(() => {
    if (isEditing && selectedInput) {
      setFormData({
        Name: selectedInput.Name || '',
        InputTypeId: selectedInput.InputTypeId || 0,
        Active: selectedInput.Active ?? true,
      });
    } else if (!isEditing) {
      setFormData({
        Name: '',
        InputTypeId: 0,
        Active: true,
      });
    }
  }, [selectedInput, isEditing]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      dispatch(setValidationErrors([]));
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (isEditing && inputId) {
      const updateData: UpdateInputRequest = {
        Id: inputId,
        ...formData,
      };
      dispatch(updateInputAction(updateData));
    } else {
      const createData: CreateInputRequest = {
        ...formData,
      };
      dispatch(createInput(createData));
    }
  };

  const handleCancel = () => {
    navigate('/inputs');
  };

  const handleBack = () => {
    navigate('/inputs');
  };

  if (loading && isEditing && !selectedInput) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {validationErrors.length > 0 && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {validationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper className={classes.paper}>
        {/* Header */}
        <Box className={classes.header}>
          <Button
            startIcon={<BackIcon />}
            onClick={handleBack}
            style={{ marginBottom: 16 }}
          >
            Back to Inputs
          </Button>
          <Typography variant="h4">
            {isEditing ? 'Edit Input' : 'Add New Input'}
          </Typography>
          {isEditing && selectedInput && (
            <Typography variant="subtitle1" color="textSecondary">
              Input: {selectedInput.Name}
            </Typography>
          )}
        </Box>

        <Divider style={{ marginBottom: 24 }} />

        {/* Form */}
        <form onSubmit={handleSubmit} className={classes.form}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Name"
                variant="outlined"
                value={formData.Name}
                onChange={(e) => handleInputChange('Name', e.target.value)}
                placeholder="Enter input name"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined" required>
                <InputLabel>Input Type</InputLabel>
                <Select
                  value={formData.InputTypeId || ''}
                  onChange={(e) => handleInputChange('InputTypeId', e.target.value)}
                  label="Input Type"
                >
                  <MenuItem value="">
                    <em>Select Input Type</em>
                  </MenuItem>
                  {inputTypes.map((inputType) => (
                    <MenuItem key={inputType.Id} value={inputType.Id}>
                      {inputType.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.Active}
                    onChange={(e) => handleInputChange('Active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>

          {/* Button Group */}
          <Box className={classes.buttonGroup}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: 8 }} />
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                isEditing ? 'Update Input' : 'Create Input'
              )}
            </Button>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default InputsEdit; 